import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import CommonFormLayout from "../../OptmisedCode/Form/CommonFormLayout";
import { MAIN_URL } from "../../../Configurations/Urls";

const BASE_ROUTE = "/organization/client/organizations";
const ORGANIZATION_TYPES = ["Client", "Testing", "Demo", "Internal"];

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ClientOrganizationAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [applications, setApplications] = useState([]);
  const [clientId, setClientId] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationShortName, setOrganizationShortName] = useState("");
  const [organizationType, setOrganizationType] = useState("Client");
  const [applicationId, setApplicationId] = useState("");
  const [editionId, setEditionId] = useState("");

  const selectedApplication = useMemo(
    () =>
      applications.find(
        (row) => String(row.application_id) === String(applicationId)
      ) || null,
    [applications, applicationId]
  );

  const isInternal = String(organizationType).toLowerCase() === "internal";
  const availableEditions = selectedApplication?.editions || [];

  useEffect(() => {
    const editions = selectedApplication?.editions || [];
    if (!selectedApplication) {
      setEditionId("");
      return;
    }
    const stillValid = editions.some(
      (row) => String(row.application_edition_id) === String(editionId)
    );
    if (stillValid) {
      return;
    }
    const wantedCode = isInternal ? "PROLEED" : "CLIENT";
    const match =
      editions.find(
        (row) => String(row.code || "").toUpperCase() === wantedCode
      ) || editions[0];
    setEditionId(match ? String(match.application_edition_id) : "");
  }, [editionId, isInternal, selectedApplication]);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/client-management/organizations/create-options`,
        { headers: authHeaders() }
      );
      const clientRows = res.data?.clients || [];
      const applicationRows = res.data?.applications || [];
      setClients(clientRows);
      setApplications(applicationRows);

      const presetClient =
        searchParams.get("client_id") || searchParams.get("filter_client_id");
      if (
        presetClient &&
        clientRows.some((row) => String(row.client_id) === String(presetClient))
      ) {
        setClientId(String(presetClient));
      }

      const defaultAppId = res.data?.default_application_id
        ? String(res.data.default_application_id)
        : applicationRows[0]
          ? String(applicationRows[0].application_id)
          : "";
      const defaultApp =
        applicationRows.find(
          (row) => String(row.application_id) === defaultAppId
        ) || applicationRows[0];
      if (defaultApp) {
        setApplicationId(String(defaultApp.application_id));
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load create options."
      );
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const handleApplicationChange = (nextApplicationId) => {
    setApplicationId(nextApplicationId);
  };

  const handleSave = async () => {
    if (!clientId) {
      toast.error("Select a client.");
      return;
    }
    if (!organizationName.trim()) {
      toast.error("Organization name is required.");
      return;
    }
    if (!organizationShortName.trim()) {
      toast.error("Organization short name is required.");
      return;
    }
    if (!applicationId) {
      toast.error("Select an application.");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${MAIN_URL}/api/client-management/organizations`,
        {
          client_id: Number(clientId),
          organization_name: organizationName.trim(),
          organization_short_name: organizationShortName.trim(),
          organization_type: organizationType,
          application_id: Number(applicationId),
          application_edition_id: editionId ? Number(editionId) : null,
        },
        { headers: authHeaders() }
      );
      toast.success("Organization created.");
      navigate(
        { pathname: BASE_ROUTE, search: location.search },
        {
          replace: true,
          state: { refreshOrganizations: Date.now() },
        }
      );
    } catch (err) {
      const errors = err.response?.data?.errors;
      const firstError =
        errors && typeof errors === "object"
          ? Object.values(errors).flat()[0]
          : null;
      toast.error(
        firstError ||
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Create failed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonFormLayout
      mode="add"
      loading={loading}
      btnLoading={saving}
      onSubmit={handleSave}
      wrapInGrid={false}
      headerProps={{
        addMessage: "Add client organization",
        updateMessage: "Client organization",
        homeLink: BASE_ROUTE,
        homeText: "Client organizations",
      }}
    >
      <div className="ems-fm-grid">
        <div className="ems-fm-field">
          <label>
            Client <span className="ems-fm-req">*</span>
          </label>
          <select
            className="ems-fm-input"
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
          >
            <option value="">Select client</option>
            {clients.map((client) => (
              <option key={client.client_id} value={String(client.client_id)}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>

        <div className="ems-fm-field">
          <label>
            Organization name <span className="ems-fm-req">*</span>
          </label>
          <input
            className="ems-fm-input"
            value={organizationName}
            onChange={(event) => setOrganizationName(event.target.value)}
          />
        </div>

        <div className="ems-fm-field">
          <label>
            Organization short name <span className="ems-fm-req">*</span>
          </label>
          <input
            className="ems-fm-input"
            value={organizationShortName}
            onChange={(event) => setOrganizationShortName(event.target.value)}
          />
        </div>

        <div className="ems-fm-field">
          <label>
            Organization type <span className="ems-fm-req">*</span>
          </label>
          <select
            className="ems-fm-input"
            value={organizationType}
            onChange={(event) => setOrganizationType(event.target.value)}
          >
            {ORGANIZATION_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="ems-fm-field">
          <label>
            Application <span className="ems-fm-req">*</span>
          </label>
          <select
            className="ems-fm-input"
            value={applicationId}
            onChange={(event) => handleApplicationChange(event.target.value)}
          >
            <option value="">Select application</option>
            {applications.map((application) => (
              <option
                key={application.application_id}
                value={String(application.application_id)}
              >
                {application.application_name}
              </option>
            ))}
          </select>
        </div>

        <div className="ems-fm-field full">
          <label>Edition</label>
          <select
            className="ems-fm-input"
            value={editionId}
            onChange={(event) => setEditionId(event.target.value)}
            disabled={!selectedApplication}
          >
            <option value="">
              {selectedApplication ? "Select edition" : "Select an application first"}
            </option>
            {availableEditions.map((edition) => (
              <option
                key={edition.application_edition_id}
                value={String(edition.application_edition_id)}
              >
                {edition.edition_name}
                {edition.code ? ` (${edition.code})` : ""}
              </option>
            ))}
          </select>
          <p className="ems-fd-sub">
            Choose any edition for this organization. Type does not lock the edition.
          </p>
        </div>
      </div>
    </CommonFormLayout>
  );
}
